/**
 * LanguageSwitcher Component
 * Toggle between English and German languages
 */

import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface LanguageSwitcherProps {
  /** Additional class names */
  className?: string;
}

const languages = [
  { code: 'en', label: 'EN' },
  { code: 'de', label: 'DE' },
] as const;

/**
 * Language switcher component for toggling between EN and DE
 */
export function LanguageSwitcher({ className }: LanguageSwitcherProps) {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language?.split('-')[0] || 'en';

  const handleLanguageChange = (langCode: string) => {
    i18n.changeLanguage(langCode);
  };

  return (
    <div
      className={cn('flex items-center gap-1', className)}
      role="group"
      aria-label="Language selection"
    >
      {languages.map((lang, index) => (
        <span key={lang.code} className="flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleLanguageChange(lang.code)}
            className={cn(
              'px-2 py-1 h-auto text-sm font-medium transition-colors cursor-pointer',
              currentLanguage === lang.code
                ? 'text-[var(--color-charcoal)]'
                : 'text-[var(--color-charcoal)]/50 hover:text-[var(--color-charcoal)]'
            )}
            aria-pressed={currentLanguage === lang.code}
            aria-label={lang.code === 'en' ? 'English' : 'Deutsch'}
          >
            {lang.label}
          </Button>
          {index < languages.length - 1 && (
            <span className="text-[var(--color-charcoal)]/30" aria-hidden="true">|</span>
          )}
        </span>
      ))}
    </div>
  );
}

export default LanguageSwitcher;
